import { createTheme } from '@mui/material/styles';

// Extend MUI palette to include custom colors
declare module '@mui/material/styles' {
  interface Palette {
    logo: Palette['primary'];
    link: Palette['primary'];
    header: Palette['primary'];
  }
  interface PaletteOptions {
    logo?: PaletteOptions['primary'];
    link?: PaletteOptions['primary'];
    header?: PaletteOptions['primary'];
  }
  interface TypographyVariants {
    logo: React.CSSProperties;
    logoTagline: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    logo?: React.CSSProperties;
    logoTagline?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    logo: true;
    logoTagline: true;
  }
}

const theme = createTheme({
  typography: {
    fontFamily: ['Inter', 'sans-serif'].join(','),
    button: { textTransform: 'none' },
    body1: { fontSize: '0.875rem' },
    logo: { fontFamily: ['Ubuntu', 'sans-serif'].join(',') },
    logoTagline: { fontFamily: ['Noto Serif Display', 'serif'].join(',') },
    subtitle1: { fontSize: '1.125rem', fontWeight: 500 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 500 },
  },
  palette: {
    primary: { main: '#000040', light: '#000080', contrastText: '#fff' },
    warning: { main: '#FF7C00' },
    success: { main: '#006B00' },
    error: { main: '#D00000' },
    text: { primary: '#1e1e1e' },
    logo: { main: '#000080', contrastText: '#fff' },
    link: { main: '#168eff' },
    header: { main: '#DAE8FF' },
    background: { default: '#fff' },
    grey: { 300: '#d8d8d8', 500: '#808080', 600: '#666' },
    divider: '#d8d8d8',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'h1, h2, h3, h4, h5, h6': { margin: 0, padding: 0 },
        p: { margin: 0 },
        ul: { margin: 0, paddingLeft: 20 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          padding: 0,
          fontSize: '0.875rem',
          maxWidth: '100%',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d8d8d8' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#d8d8d8' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#d8d8d8', borderWidth: 1 },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#D00000', borderWidth: 1 },
          '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#D00000' },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: '#d8d8d8', cursor: 'default', opacity: 0.6 },
        },
        input: { padding: 8, borderRadius: '8px', fontSize: '0.875rem' },
        multiline: { borderRadius: '8px', fontSize: '0.875rem' },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: { padding: 8, borderRadius: '8px', fontSize: '0.875rem' },
        root: {
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#D00000' },
          '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#D00000' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#fff' } } },
    MuiMenuItem: { styleOverrides: { root: { fontSize: '0.875rem' } } },
    MuiSwitch: {
      styleOverrides: {
        root: { width: 28, height: 16, padding: 0, display: 'flex', alignItems: 'center' },
        switchBase: {
          padding: 4,
          '&.Mui-checked': {
            transform: 'translateX(12px)',
            '& + MuiSwitch-track': { backgroundColor: '#000080', border: '2px solid #000080', opacity: 1 },
            '& .MuiSwitch-thumb': { backgroundColor: '#fff' },
          },
        },
        thumb: { boxShadow: 'none', width: 8, height: 8, backgroundColor: '#000080' },
        track: { borderRadius: 16, backgroundColor: '#fff', border: '2px solid #000080', opacity: '1 !important' as any },
      },
    },
    MuiStepConnector: { styleOverrides: { line: { borderWidth: 2 } } },
    MuiCheckbox: { styleOverrides: { root: { '&.Mui-checked': { color: 'var(--color-gray-dark)' } } } },
  },
});

export default theme;
